import React, { useState } from 'react';
import styled from 'styled-components';
import Panel from './Panel';
import PeriodSelector from './PeriodSelector';
import Pagination from './Pagination';
import useMonitoringData from '../hooks/useMonitoring';
import { fetchSubscribeHistory } from '../api/monitoringApi';

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; color: #6b7280; font-weight: 600; white-space: nowrap;`;
const Td = styled.td`padding: 8px; border-bottom: 1px solid #f3f4f6; color: #374151; white-space: nowrap;`;

const PlanBadge = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600;
  background: ${p => {
    switch(p.$plan) {
      case 'free_trial': return '#f3f4f6';
      case 'starter': return '#dbeafe';
      case 'standard': return '#d1fae5';
      case 'premium': return '#fef3c7';
      default: return '#f3f4f6';
    }
  }};
  color: ${p => {
    switch(p.$plan) {
      case 'free_trial': return '#6b7280';
      case 'starter': return '#1d4ed8';
      case 'standard': return '#047857';
      case 'premium': return '#b45309';
      default: return '#6b7280';
    }
  }};
`;

const PaidBadge = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600;
  background: ${p => p.$paid ? '#d1fae5' : '#f3f4f6'};
  color: ${p => p.$paid ? '#047857' : '#9ca3af'};
`;

const Summary = styled.div`display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap;`;
const SummaryItem = styled.div`font-size: 13px; color: #6b7280; span { font-weight: 700; color: #111827; }`;

const FilterRow = styled.div`display: flex; gap: 8px; margin-bottom: 12px; align-items: center;`;
const Select = styled.select`padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12px;`;

function fmtDate(v) { return v || '-'; }

export default function SubscribeHistoryPanel({ refreshKey }) {
  const [period, setPeriod] = useState('30d');
  const [planType, setPlanType] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useMonitoringData(
    () => fetchSubscribeHistory(period, planType || undefined, page, 20),
    [period, planType, page, refreshKey]
  );

  const handlePeriod = (p) => { setPeriod(p); setPage(1); };

  return (
    <Panel title="구독 발생 이력" isLoading={isLoading} error={error}
      actions={<PeriodSelector value={period} onChange={handlePeriod} options={['7d','30d','90d']} />}>
      {data && <>
        <FilterRow>
          <Select value={planType} onChange={e => { setPlanType(e.target.value); setPage(1); }}>
            <option value="">전체 플랜</option>
            <option value="free_trial">Free Trial</option>
            <option value="starter">Starter</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
          </Select>
        </FilterRow>
        <Summary>
          <SummaryItem>총 구독: <span>{data.total_count}건</span></SummaryItem>
          <SummaryItem>유료: <span>{data.paid_count}건</span></SummaryItem>
          <SummaryItem>무료: <span>{data.free_count}건</span></SummaryItem>
        </Summary>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr>
              <Th>고객</Th><Th>사이트</Th><Th>플랜</Th><Th>결제</Th><Th>금액</Th><Th>상태</Th><Th>시작일</Th><Th>종료일</Th><Th>생성일</Th>
            </tr></thead>
            <tbody>
              {data.records?.map((r, i) => (
                <tr key={i}>
                  <Td>{r.client_name || r.client_email}</Td>
                  <Td>{r.site_name}<br/><span style={{fontSize:11,color:'#9ca3af'}}>{r.domain}</span></Td>
                  <Td><PlanBadge $plan={r.plan_type}>{r.plan_type}</PlanBadge></Td>
                  <Td><PaidBadge $paid={r.is_paid}>{r.is_paid ? '유료' : '무료'}</PaidBadge></Td>
                  <Td>{r.is_paid && r.payment_amount ? `₩${Number(r.payment_amount).toLocaleString()}` : '-'}</Td>
                  <Td>{r.status}{r.pending_downgrade && <span style={{color:'#f59e0b',fontSize:11}}> ({r.pending_downgrade})</span>}</Td>
                  <Td>{fmtDate(r.start_date)}</Td>
                  <Td>{fmtDate(r.end_date)}</Td>
                  <Td>{r.created_at ? new Date(r.created_at).toLocaleDateString('ko-KR') : '-'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Pagination page={data.page} totalPages={data.total_pages} onChange={setPage} />
      </>}
    </Panel>
  );
}
